import type { OAuth2Config } from '@thinkalpha/ui-types/types/oauth';
import type { KeycloakConfig } from '@thinkalpha/ui-types/types/schemas/OAuthConfigSchema';
import type { RuntimeEnvPayload } from 'src/services/workerHost/envelope';

declare global {
    const APP_COMMIT: string;
    const APP_CONFIG_NAME: string; // TODO: deprecate; use "environment" terminology
    const APP_VERSION: string;

    interface Window {
        appConfig: ConfigType;
    }
}

/**
 * Resolve config values from the injected `globalThis.__APP_ENV__` (app-ws serve-time script on
 * the main thread; the worker kernel's init-frame seeding in worker scopes — ADR 0016) with
 * fallback to build-time Vite globals (legacy per-env builds). When `__APP_ENV__` is present it is
 * the sole runtime authority. When it is absent, the hostname fallback below applies (see
 * `getHrefEnv`). typeof guards prevent ReferenceError in contexts where Vite's define hasn't run.
 */
const runtimeEnv = globalThis.__APP_ENV__;

type HrefEnv = 'prod' | 'prod-alpha' | 'dev' | 'qa' | 'uat';

/**
 * Derive the environment from the current origin's host.
 *
 * Hybrid fallback for the ADR 0016 migration window (PLAT-13159, PLAT-13158): deployed builds are
 * environment-agnostic and learn their environment only from `__APP_ENV__`, which app-ws injects
 * on the main thread and the worker kernel seeds from the `init` frame. Workers that have not been
 * converted to the kernel (Alert, OptionsList, Locates) never receive it, and without this fallback
 * they resolve the `local` config and call the dev environment's APIs with another environment's
 * token. A worker's `self.location` is its script URL on the spawning page's origin, so the host
 * still names the environment. Only consulted when `__APP_ENV__` is absent; deleted with the last
 * worker conversion (ADR 0016 increment ③).
 */
export const getHrefEnv = (): HrefEnv | undefined => {
    if (APP_ELECTRON) return undefined;
    if (typeof location === 'undefined') return undefined;

    const { host } = location;
    if (host === 'askalpha.io' || host === 'www.askalpha.io') return 'prod';
    if (host === 'lens.thinkalpha.io') return 'prod';
    if (host.endsWith('uat.thinkalpha.io')) return 'uat';
    if (host.endsWith('qa.thinkalpha.io')) return 'qa';
    if (host.endsWith('prod-alpha.thinkalpha.io')) return 'prod-alpha';
    if (host.endsWith('dev.thinkalpha.io')) return 'dev';
    if (host.endsWith('prod.thinkalpha.io')) return 'prod';
    if (host.endsWith('platform.thinkalpha.io')) return 'prod';

    return undefined;
};

const hrefEnv = runtimeEnv ? undefined : getHrefEnv();

const resolvedConfigName =
    runtimeEnv?.APP_CONFIG_NAME ?? (typeof APP_CONFIG_NAME !== 'undefined' ? APP_CONFIG_NAME : 'local');
const resolvedCommit = runtimeEnv?.APP_COMMIT ?? (typeof APP_COMMIT !== 'undefined' ? APP_COMMIT : 'unknown');
const resolvedVersion = runtimeEnv?.APP_VERSION ?? (typeof APP_VERSION !== 'undefined' ? APP_VERSION : undefined);

/**
 * The resolved runtime environment triple, exactly as this module resolved it — what
 * `connectWorker` delivers to workers in the `init` frame (ADR 0016). Carries the hostname
 * fallback when it applied, so a kernel worker agrees with the host that spawned it.
 */
export const runtimeEnvTriple = {
    APP_CONFIG_NAME: hrefEnv ?? resolvedConfigName,
    APP_COMMIT: resolvedCommit,
    ...(resolvedVersion !== undefined ? { APP_VERSION: resolvedVersion } : {}),
} satisfies RuntimeEnvPayload;

/**
 * Sampling rate for production environments.
 */
const prodSampleRate = 10.0;

export interface ConfigType {
    accessApi: string;
    api: string;
    appVersion: string | undefined;
    commit: string;
    documentTitle: string;
    nlpApi: string;
    environmentName: string;
    i18nVersion: string;
    gaussApi: string;
    tradingApi: string;
    tracesSampleRate: number;
    backtestSettingsSchemaVersion: number;
    bardexApi: string;
    /**
     * URI of the legacy locates deployment. When specified, UI uses it instead of platform-ws.
     */
    legacyLocatesApi?: string;
    locatesApi?: string;
    /**
     * OAuth2 config
     */
    oAuth2Config: OAuth2Config;
    tableProxy: string;
    httpNgTableProxy: string;
    wsNgTableProxy: string;
    /**
     * Launch Darkly Config
     */
    launchDarklyClientSideId: string;
    newQuantApi: string;
    /**
     * Stripe publishable key for payment elements
     */
    stripePublishableKey: string;
}

/**
 *
 * @param env the name of the environment; must be a valid API environment
 * @returns partial configuration object
 */
const shared = (env: string) =>
    ({
        /**
         * APIS (env-driven)
         */
        accessApi: `https://access-ws.${env}.thinkalpha.io`,
        api: `https://api.${env}.thinkalpha.io`,
        documentTitle: 'DEV alphaLENS',
        gaussApi: `https://gauss-ws.${env}.thinkalpha.io`,
        // Dev (including the local fallback) and QA use the trading gateway; UAT and prod use trading-ws.
        tradingApi:
            env === 'dev' || env === 'qa'
                ? `https://trading-gateway-api.${env}.thinkalpha.io/`
                : `https://trading-ws.${env}.thinkalpha.io`,
        nlpApi: `https://nlp.${env}.thinkalpha.io`,
        bardexApi: `https://bardex.${env}.thinkalpha.io`,
        environmentName: `${env}`,
        commit: resolvedCommit,
        /**
         * VARS (base, expect overrides)
         */
        tracesSampleRate: 1.0,
        appVersion: undefined,
        backtestSettingsSchemaVersion: 1,
        locatesApi: `https://locates-ws.${env}.thinkalpha.io`,
        legacyLocatesApi: undefined, // only available in QA and above,
        tableProxy: `https://table-ws.${env}.thinkalpha.io`,
        httpNgTableProxy: `https://table.${env}.thinkalpha.io`,
        wsNgTableProxy: `wss://table-socket.${env}.thinkalpha.io`,
        launchDarklyClientSideId: '660c29bfb2e0e70fe6409c86',
        newQuantApi: `https://quant-ws.${env}.thinkalpha.io`,
    }) satisfies Partial<ConfigType>;

const makeKeycloakConfig = ({
    keycloakURL,
    tenantID,
    applicationID,
}: {
    keycloakURL: string;
    tenantID: string;
    applicationID: string;
}): KeycloakConfig => {
    let applicationURL;
    if (APP_ELECTRON) {
        applicationURL = 'https://dev.thinkalpha.io';
    } else if (typeof WorkerGlobalScope !== 'undefined' && self instanceof WorkerGlobalScope) {
        applicationURL = self.location.origin;
    } else {
        applicationURL = window.location.origin;
    }
    return {
        type: 'keycloak',
        keycloakURL,
        userInfoURL: `${keycloakURL}/${tenantID}/protocol/openid-connect/userinfo`,
        logoutURL: `${keycloakURL}/${tenantID}/protocol/openid-connect/logout`,
        tokenRevocationURL: `${keycloakURL}/${tenantID}/protocol/openid-connect/revoke`,
        tenantID,
        applicationID,
        callbackURL: `${applicationURL}/oauth2/callback`,
        loggedOutURL: `${applicationURL}/loggedout`,
    };
};

const makeDevOAuth2Config: () => KeycloakConfig = () =>
    makeKeycloakConfig({
        keycloakURL: 'https://auth.dev.thinkalpha.io',
        tenantID: 'realms/thinkalpha',
        applicationID: 'platform-ui',
    });

/**
 * Apply the hostname fallback: when the host names an environment other than the one the config
 * was selected by, overlay that environment's service URLs and name. Returns `config` unchanged
 * when there is nothing to apply. Pure so the overlay is unit-testable without re-evaluating the
 * module.
 */
export function applyHrefEnvFallback(
    config: ConfigType,
    hostEnv: HrefEnv | undefined,
    selectedConfigName: string,
): ConfigType {
    if (!hostEnv || hostEnv === selectedConfigName) return config;
    return { ...config, ...shared(hostEnv) };
}

/**
 * Check if we're running in a test environment
 */
const isTestEnvironment = (): boolean => {
    // Vitest sets either import.meta.env.VITEST or import.meta.vitest
    if (typeof import.meta !== 'undefined') {
        const m = import.meta as any;
        if (m.env?.VITEST || m.vitest) return true;
    }
    // Fallback for Node-based runners
    return typeof process !== 'undefined' && !!process.env?.VITEST;
};

let appConfig: ConfigType;
try {
    const ENV_TO_USE = resolvedConfigName;

    if (ENV_TO_USE === 'prod') {
        appConfig = {
            ...shared('prod'),
            documentTitle: 'alphaLENS',
            tracesSampleRate: prodSampleRate,
            legacyLocatesApi: 'wss://locates.thinkalpha.io',
            oAuth2Config: makeKeycloakConfig({
                keycloakURL: 'https://auth.prod.thinkalpha.io',
                tenantID: 'realms/thinkalpha',
                applicationID: 'platform-ui',
            }),
            i18nVersion: 'prod',
            launchDarklyClientSideId: '660c29bfb2e0e70fe6409c87',
            stripePublishableKey:
                'pk_live_51SmgWTDvYDgvvI7uVFBHZKS0ThBT25qcdx3DWA5f1H8ejaKPA2buzH2nThgfgFlDsIB7D2XtAu1KcxzEx73S0Nby00wqJEhXzl',
        };
    } else if (ENV_TO_USE === 'dev') {
        appConfig = {
            ...shared('dev'),
            i18nVersion: 'latest',
            legacyLocatesApi: 'wss://locates-and-preborrows-app.dev.ny.thinkalpha.io',
            oAuth2Config: makeDevOAuth2Config(),
            launchDarklyClientSideId: '660c29bfb2e0e70fe6409c86',
            stripePublishableKey:
                'pk_test_51SrmKHDS1eElTTUgwj0xKizbbFgXrzSaprR9OnWJo6PtnRQvzXvuDo6gGcrSQHAm6UyvI9ieddd15PGt2MzDsXvI00KlrQgYZq',
        };
    } else if (ENV_TO_USE === 'qa') {
        appConfig = {
            ...shared('qa'),
            i18nVersion: 'qa',
            documentTitle: 'QA alphaLENS',
            // legacyLocatesApi: 'wss://qalocates.thinkalpha.io',
            oAuth2Config: makeKeycloakConfig({
                keycloakURL: 'https://auth.qa.thinkalpha.io',
                tenantID: 'realms/thinkalpha',
                applicationID: 'platform-ui',
            }),
            launchDarklyClientSideId: '660c436987af650fef869be1',
            stripePublishableKey:
                'pk_test_51SrnwlDzho1vODWuQNXls9WKR2QIbSsci2zw00AbneA1sOCBYgxqlrQhNBzzM14CZE1sUL5po9idd53Mug4r6COs00l6iA4ViJ',
        };
    } else if (ENV_TO_USE === 'uat') {
        appConfig = {
            ...shared('uat'),
            i18nVersion: 'uat',
            documentTitle: 'UAT alphaLENS',
            tracesSampleRate: prodSampleRate / 2,
            legacyLocatesApi: 'wss://uatlocates.thinkalpha.io',
            oAuth2Config: makeKeycloakConfig({
                keycloakURL: 'https://auth.uat.thinkalpha.io',
                tenantID: 'realms/thinkalpha',
                applicationID: 'platform-ui',
            }),
            launchDarklyClientSideId: '660c43746ca8d50f6f275d95',
            stripePublishableKey:
                'pk_test_51SrnxuDARSYIYP4k5Se8QbjI6vhbak1SmIcKsaGD9b3f29PUNMF9hI5oYc4h18EkpuK4CtaDMn15jl2HVC4LTrMg009cjF7byy',
        };
    } else {
        appConfig = {
            ...shared('dev'),
            i18nVersion: 'latest',
            appVersion: 'local',
            environmentName: 'local',
            launchDarklyClientSideId: '660c29bfb2e0e70fe6409c86',
            oAuth2Config: makeDevOAuth2Config(),
            stripePublishableKey:
                'pk_test_51SrmKHDS1eElTTUgwj0xKizbbFgXrzSaprR9OnWJo6PtnRQvzXvuDo6gGcrSQHAm6UyvI9ieddd15PGt2MzDsXvI00KlrQgYZq',
        };
    }

    if (import.meta.env.VITE_API_URL) {
        appConfig.api = import.meta.env.VITE_API_URL; // 'https://localhost:3000';
    }
    if (import.meta.env.VITE_TRADING_API_URL) {
        appConfig.tradingApi = import.meta.env.VITE_TRADING_API_URL; // 'https://localhost:3001';
    }
    if (import.meta.env.VITE_ACCESS_API_URL) {
        appConfig.accessApi = import.meta.env.VITE_ACCESS_API_URL; // 'https://localhost:3002';
    }
    if (import.meta.env.VITE_QUANT_API_URL) {
        appConfig.newQuantApi = import.meta.env.VITE_QUANT_API_URL; // 'https://localhost:3003';
    }
    if (import.meta.env.VITE_PROXY_URL) {
        appConfig.tableProxy = import.meta.env.VITE_PROXY_URL; // 'https://localhost:3005';
    }

    // Hostname fallback for scopes without `__APP_ENV__` (unconverted workers); see getHrefEnv.
    appConfig = applyHrefEnvFallback(appConfig, hrefEnv, ENV_TO_USE);

    // Override all URLs with mock URLs when running in test environment
    if (isTestEnvironment()) {
        appConfig = {
            ...appConfig,
            // Avoid accidental network initializations in tests
            launchDarklyClientSideId: 'test',
            accessApi: 'http://localhost:9999/mock-access-api',
            api: 'http://localhost:9999/mock-api',
            gaussApi: 'http://localhost:9999/mock-gauss-api',
            tradingApi: 'http://localhost:9999/mock-trading-api',
            nlpApi: 'http://localhost:9999/mock-nlp-api',
            locatesApi: 'http://localhost:9999/mock-locates-api',
            legacyLocatesApi: 'ws://localhost:9999/mock-legacy-locates-api',
            tableProxy: 'http://localhost:9999/mock-table-proxy',
            newQuantApi: 'http://localhost:9999/mock-new-quant-api',
            environmentName: 'test',
            documentTitle: 'TEST alphaLENS',
            stripePublishableKey:
                'pk_test_51SrmKHDS1eElTTUgwj0xKizbbFgXrzSaprR9OnWJo6PtnRQvzXvuDo6gGcrSQHAm6UyvI9ieddd15PGt2MzDsXvI00KlrQgYZq',
            // Use a mock OAuth config for tests
            oAuth2Config: makeKeycloakConfig({
                keycloakURL: 'http://localhost:9999/mock-auth',
                tenantID: 'test-realm',
                applicationID: 'test-platform-ui',
            }),
        };
    }
} catch (e) {
    if (e instanceof ReferenceError) {
        // logger.error({
        //     message: 'Failed to identify the app config name! Defaulting to dev.',
        // });
        appConfig = {
            ...shared('dev'),
            oAuth2Config: makeDevOAuth2Config(),
            i18nVersion: 'latest',
            stripePublishableKey:
                'pk_test_51SrmKHDS1eElTTUgwj0xKizbbFgXrzSaprR9OnWJo6PtnRQvzXvuDo6gGcrSQHAm6UyvI9ieddd15PGt2MzDsXvI00KlrQgYZq',
        };
    } else {
        throw e;
    }
}

appConfig.appVersion = resolvedVersion ?? appConfig.appVersion;

if (typeof window !== 'undefined') {
    window.appConfig = appConfig;
}

export { appConfig };
